import PropTypes from 'prop-types';
// CORE
import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// MUI
import CircularProgress from '@mui/material/CircularProgress';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ExcludeFromReports from 'assets/icons/settings-menu/ExcludeFromReports.inline.svg';
import ExcludeFromSpendingPlan from 'assets/icons/settings-menu/ExcludeFromSpendingPlan.inline.svg';
import { makeStyles } from 'tss-react/mui';

import { a2aTransfersSelectors } from 'app-components/flux/a2aTransfers';
// BIT
import { accountsTypes, accountsUtils } from 'app-components/flux/accounts';
import { businessSelectors } from 'app-components/flux/business';
import { institutionLoginsSelectors } from 'app-components/flux/institution-logins';
import { isDemoMode } from 'app-components/utils/core';
import * as fundingAccountSelectors from 'data/fundingAccounts/selectors';

import AccountStatusChipList from 'components/Chips/AccountStatusChipList';
import { showMicrodespositDialog } from 'components/Dialogs/A2A/actions';
import AccountMenu from 'components/AccountMenu';
// LOCAL
import Dump from 'components/Dump';
import QTip from 'components/QuickenControls/QTip';
import BusinessIcon from 'containers/BusinessCard/BusinessIcon';

import styles from './styles';

const useStyles = makeStyles()(styles);

const AccountListItem = (props) => {
  const { account, index, institutionLogin, errorBannerShowing } = props;

  const dispatch = useDispatch();
  const { classes } = useStyles();

  // ====================================== Redux Selectors ================================================ //
  const a2aTransferEnabled = useSelector(a2aTransfersSelectors.a2aTransferEnabled);
  const business = useSelector((state) =>
    account?.businessId ? businessSelectors.getBusinessForId(state, account?.businessId) : undefined,
  );
  const isTransferSetupEligible = useSelector((state) =>
    fundingAccountSelectors.isAccountEligibleForTransfers(state, account),
  );
  const isMicrodepositEligible = useSelector((state) =>
    fundingAccountSelectors.isAccountEligibleForMicrodeposits(state, account),
  );
  const isMoveMoneyEligible = useSelector((state) =>
    fundingAccountSelectors.isAccountEligibleForCashTransfer(state, account),
  );
  const isDisabled = useSelector((state) => fundingAccountSelectors.isAccountDisabled(state, account));
  const fundingAccount = useSelector((state) =>
    fundingAccountSelectors.getFundingAccountById(state, account?.fundingAccountId),
  );

  const allInstitutionsRefreshing = useSelector(institutionLoginsSelectors.getIsRefreshingAll);
  const thisInstitutionRefreshing = useSelector(institutionLoginsSelectors.getIdsRefreshing);

  // ===================================== Redux Util Values ============================================== //
  const isError = accountsUtils.isAccountInError(account)?.isError;
  const isDisconnected = accountsUtils.isAccountDisconnected(account, institutionLogin);

  // ====================================== Callbacks ================================================ //
  const isErrorBeingFixed = () => {
    const aggStatus = account?.aggregators?.get(0)?.aggStatus;
    return (
      Boolean(institutionLogin) &&
      Boolean(thisInstitutionRefreshing.has(institutionLogin?.id)) &&
      !allInstitutionsRefreshing &&
      Boolean((aggStatus != null && aggStatus !== 'OK') || institutionLogin?.optionalAction)
    );
  };

  const kickoffMicrodepositFlow = useCallback(
    (initiator) => {
      dispatch(
        showMicrodespositDialog({
          fundingAccountId: fundingAccount?.id,
          hideAccountPicker: true,
          initiator,
        }),
      );
    },
    [fundingAccount, dispatch],
  );

  // ====================================== Memoized Values ================================================ //
  const primaryListItemText = useMemo(
    () => (
      <div className={classes.primaryItem}>
        <Typography id={`account-${index}-name`} variant='paragraphRegular'>
          {account.name}
        </Typography>
        {account.isExcludedFromReports && account?.isExcludedFromBudgets ? (
          <QTip title='Excluded from Spending Plan & Reports'>
            <ExcludeFromSpendingPlan className={classes.imgClass} id={`account-${index}-ignored-spending-plan-label`} />
            <ExcludeFromReports className={classes.imgClass} id={`account-${index}-ignored-reports-label`} />
          </QTip>
        ) : (
          <>
            {account?.isExcludedFromBudgets && (
              <QTip title='Excluded from Spending Plan'>
                <ExcludeFromSpendingPlan
                  className={classes.imgClass}
                  id={`account-${index}-ignored-spending-plan-label`}
                />
              </QTip>
            )}
            {account.isExcludedFromReports && (
              <QTip title='Excluded from Reports'>
                <ExcludeFromReports className={classes.imgClass} id={`account-${index}-ignored-reports-label`} />
              </QTip>
            )}
          </>
        )}
      </div>
    ),
    [classes.primaryItem, account, index, classes.imgClass],
  );

  const secondaryListItemText = useMemo(
    () => (
      <Stack>
        <Typography id={`account-${index}-type`} variant='paragraphRegular' className={classes.secondaryListItem}>
          {`${
            accountsTypes.getAccountTypeNodeByQcsType(account.type, account.subType).displayName
          } ${accountsUtils?.formatFinalFour(account)}`}
        </Typography>
      </Stack>
    ),
    [index, classes, account],
  );
  const secondaryTypographyProps = useMemo(() => ({ component: 'div' }), []);

  // ======================================= Account Status ====================================== //
  const accountStatus = {
    eligible: {
      showChip: a2aTransferEnabled && isTransferSetupEligible?.status, // "showChip" is used to track the status of the account
      enableTransferFunctions: a2aTransferEnabled && isTransferSetupEligible?.isEligible,
    },
    ineligible: {
      showChip: false,
      enableTransferFunctions: a2aTransferEnabled && !isTransferSetupEligible?.isEligible,
    },
    depositPending: {
      showChip: a2aTransferEnabled && isMicrodepositEligible?.status,
      enableTransferFunctions: a2aTransferEnabled && isMicrodepositEligible?.isEligible,
      manualVerification: a2aTransferEnabled && isMicrodepositEligible?.isManualVerified,
    },
    verified: {
      showChip: a2aTransferEnabled && isMoveMoneyEligible?.status,
      enableTransferFunctions: a2aTransferEnabled && isMoveMoneyEligible?.isEligible,
    },
    fundingAccountFailure: {
      showChip: a2aTransferEnabled && isDisabled?.status,
    },
    error: {
      showChip: isError,
    },
    closed: {
      showChip: account?.isClosed,
    },
    disconnected: {
      showChip: isDisconnected && !account?.isClosed && !isDemoMode(),
    },
  };

  let ineligibleText = '';
  if (accountStatus.depositPending.showChip && !accountStatus.depositPending.enableTransferFunctions) {
    ineligibleText = isMicrodepositEligible?.reasonString;
  } else if (accountStatus.verified.showChip && !accountStatus.verified.enableTransferFunctions) {
    ineligibleText = isMoveMoneyEligible?.reasonString;
  } else if (accountStatus.fundingAccountFailure.showChip) {
    ineligibleText = isDisabled.reasonString;
  } else {
    ineligibleText = isTransferSetupEligible?.reasonString;
  }

  // ============================================================================================ //

  /* The spinner should only appear if specific accounts are in an error state, not if the entire FI is in an error state. Otherwise there would be too many spinners! */
  const renderErrorResolvingSpinner = () => {
    if (isErrorBeingFixed() && !errorBannerShowing) {
      return (
        <QTip solidTheme placement='top' position='top' title='Attempting to reconnect' noTippyTip arrow enableOnMobile>
          <CircularProgress size={20} className={classes.spinner} />
        </QTip>
      );
    }
    return null;
  };

  return (
    <ListItem
      dense
      secondaryAction={
        <>
          <div className={classes.rightRowContainer}>
            {renderErrorResolvingSpinner()}
            <AccountStatusChipList
              tooltipText={ineligibleText}
              accountStatus={accountStatus}
              kickoffMicrodepositFlow={() => kickoffMicrodepositFlow('verify button')}
            />
            {business ? <BusinessIcon business={business} /> : null}
            {fundingAccount ? <Dump obj={fundingAccount} /> : null}
            <AccountMenu accountId={account.id} errorBannerShowing={errorBannerShowing} />
          </div>
        </>
      }
    >
      <Dump obj={account} />
      <ListItemText
        primary={primaryListItemText}
        secondary={secondaryListItemText}
        secondaryTypographyProps={secondaryTypographyProps}
      />
    </ListItem>
  );
};
AccountListItem.propTypes = {
  account: PropTypes.object,
  index: PropTypes.number,
  institutionLogin: PropTypes.object,
  errorBannerShowing: PropTypes.bool,
};

export default AccountListItem;
